import * as THREE from "three";

export const FLOW = 2.2;
export const FOG_HEX = 0x090d14;
const hexVec = (hex) => new THREE.Vector3(((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255);

export const waveHeight = (x, z, t, amp = 1) => {
  const X = x + t * FLOW;
  return amp * (Math.sin(X * 0.06 + t * 0.6) * 0.55 + Math.sin(z * 0.09 + t * 0.8 + X * 0.02) * 0.38 + Math.sin((X + z) * 0.15 + t * 1.3) * 0.14);
};

const OCEAN_VERT = /* glsl */ `
uniform float uTime; uniform float uAmp; uniform float uFlow;
varying vec3 vWorld;
float wave(vec2 p, float t){
  float X = p.x + t * uFlow;
  return uAmp * (sin(X*0.06 + t*0.6)*0.55 + sin(p.y*0.09 + t*0.8 + X*0.02)*0.38 + sin((X+p.y)*0.15 + t*1.3)*0.14);
}
void main(){
  vec4 w = modelMatrix * vec4(position, 1.0);
  w.y += wave(w.xz, uTime);
  vWorld = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

const OCEAN_FRAG = /* glsl */ `
uniform float uTime; uniform float uFlow; uniform float uRings; uniform float uSweep; uniform float uIntro;
uniform float uGrid; uniform float uWake; uniform float uFogDensity;
uniform vec3 uBase; uniform vec3 uCyan; uniform vec3 uFogColor;
varying vec3 vWorld;
void main(){
  vec3 n = normalize(cross(dFdx(vWorld), dFdy(vWorld)));
  if (n.y < 0.0) n = -n;
  vec3 V = normalize(cameraPosition - vWorld);
  vec3 L = normalize(vec3(-0.55, 0.5, -0.45));
  float diff = clamp(dot(n, L), 0.0, 1.0);
  float fres = pow(1.0 - clamp(dot(n, V), 0.0, 1.0), 4.0);
  vec3 col = uBase * (0.35 + 0.9 * diff) + vec3(0.05, 0.075, 0.1) * fres;
  vec3 H = normalize(L + V);
  col += vec3(0.6, 0.72, 0.85) * pow(clamp(dot(n, H), 0.0, 1.0), 140.0) * 0.55;

  vec2 p = vWorld.xz;
  float r = length(p);
  vec2 g = (p + vec2(uTime * uFlow, 0.0)) / 7.0;
  vec2 gw = max(fwidth(g), vec2(1e-4));
  vec2 gl = abs(fract(g - 0.5) - 0.5) / gw;
  float line = 1.0 - min(min(gl.x, gl.y), 1.0);
  col += uCyan * line * 0.06 * uGrid * exp(-r * 0.009) * uIntro;

  float rr = abs(fract(r / 20.0 + 0.5) - 0.5) * 20.0;
  float ringLine = 1.0 - smoothstep(0.0, fwidth(r) * 1.6, rr);
  float ang = atan(p.y, p.x);
  float dash = step(0.45, fract(ang * 36.0 / 6.2831853));
  float within = 1.0 - smoothstep(50.0, 82.0, r);
  col += uCyan * ringLine * dash * 0.26 * within * uRings * uIntro * step(15.0, r);

  float pr = fract(uTime * 0.16) * 95.0;
  float pulse = exp(-abs(r - pr) * 0.8) * (1.0 - pr / 95.0) * step(12.0, r);
  col += uCyan * pulse * 0.32 * uRings * uIntro;

  float sa = mod(uTime * 0.85, 6.2831853);
  float d = mod(sa - ang + 6.2831853, 6.2831853);
  float trail = exp(-d * 2.4) * (1.0 - smoothstep(26.0, 76.0, r)) * step(13.0, r);
  col += uCyan * trail * 0.17 * uSweep * uIntro;

  float ax = -p.x - 31.0;
  if (ax > 0.0) {
    float spread = 2.2 + ax * 0.2;
    float wk = exp(-abs(p.y) / spread * 1.7) * exp(-ax * 0.02);
    col += vec3(0.55, 0.65, 0.74) * wk * 0.2 * uWake * (0.65 + 0.35 * sin(ax * 0.55 - uTime * 3.0));
  }
  float bowx = p.x - 30.0;
  col += vec3(0.6, 0.7, 0.78) * exp(-abs(bowx) * 0.5) * exp(-max(abs(p.y) - 5.4, 0.0) * 0.6) * 0.12 * uWake * step(abs(p.y), 9.0);

  float dist = length(cameraPosition - vWorld);
  float fog = 1.0 - exp(-pow(uFogDensity * dist, 2.0));
  col = mix(col, uFogColor, clamp(fog, 0.0, 1.0));
  gl_FragColor = vec4(col, 1.0);
}`;

const SKY_VERT = /* glsl */ `
varying vec3 vDir;
void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const SKY_FRAG = /* glsl */ `
uniform vec3 uHorizon; uniform vec3 uTop; uniform vec3 uMoonDir;
varying vec3 vDir;
void main(){
  vec3 d = normalize(vDir);
  vec3 col = mix(uHorizon, uTop, smoothstep(-0.02, 0.45, d.y));
  float m = max(dot(d, normalize(uMoonDir)), 0.0);
  col += vec3(0.55, 0.65, 0.78) * (pow(m, 900.0) * 1.2 + pow(m, 40.0) * 0.06 + pow(m, 6.0) * 0.025);
  gl_FragColor = vec4(col, 1.0);
}`;

export const makeHalo = () => {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d");
  const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grd.addColorStop(0, "rgba(255,255,255,1)");
  grd.addColorStop(0.25, "rgba(255,255,255,0.45)");
  grd.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grd;
  g.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
};

export function createWorld(scene, { segments = 200, rings = 1, sweep = 1, grid = 1, wake = 1, fogDensity = 0.0062 } = {}) {
  const trash = [];
  const keep = (o) => { trash.push(o); return o; };
  scene.fog = new THREE.FogExp2(FOG_HEX, fogDensity);
  const oceanGeo = keep(new THREE.PlaneGeometry(760, 760, segments, segments));
  oceanGeo.rotateX(-Math.PI / 2);
  const uniforms = {
    uTime: { value: 0 }, uAmp: { value: 1 }, uFlow: { value: FLOW }, uRings: { value: rings }, uSweep: { value: sweep },
    uIntro: { value: 1 }, uGrid: { value: grid }, uWake: { value: wake }, uFogDensity: { value: fogDensity },
    uBase: { value: hexVec(0x122233) }, uCyan: { value: hexVec(0x00f2fe) }, uFogColor: { value: hexVec(FOG_HEX) },
  };
  const ocean = new THREE.Mesh(oceanGeo, keep(new THREE.ShaderMaterial({ uniforms, vertexShader: OCEAN_VERT, fragmentShader: OCEAN_FRAG })));
  scene.add(ocean);

  const skyGeo = keep(new THREE.SphereGeometry(560, 32, 16));
  const sky = new THREE.Mesh(skyGeo, keep(new THREE.ShaderMaterial({
    uniforms: { uHorizon: { value: hexVec(FOG_HEX) }, uTop: { value: hexVec(0x030508) }, uMoonDir: { value: new THREE.Vector3(-0.62, 0.32, -0.7) } },
    vertexShader: SKY_VERT, fragmentShader: SKY_FRAG, side: THREE.BackSide, depthWrite: false, fog: false,
  })));
  scene.add(sky);

  const starPos = [];
  for (let i = 0; i < 900; i++) {
    const th = Math.random() * Math.PI * 2;
    const ph = Math.acos(1 - Math.random() * 0.85);
    starPos.push(Math.sin(ph) * Math.cos(th) * 520, Math.cos(ph) * 520 + 20, Math.sin(ph) * Math.sin(th) * 520);
  }
  const starGeo = keep(new THREE.BufferGeometry());
  starGeo.setAttribute("position", new THREE.Float32BufferAttribute(starPos, 3));
  scene.add(new THREE.Points(starGeo, keep(new THREE.PointsMaterial({ color: 0xc4d4e4, size: 1.3, sizeAttenuation: false, transparent: true, opacity: 0.55, fog: false }))));

  scene.add(new THREE.HemisphereLight(0x8aa2bd, 0x05070a, 2.1));
  const moon = new THREE.DirectionalLight(0xcfe0f2, 3.6);
  moon.position.set(-70, 60, -60);
  scene.add(moon);
  const fill = new THREE.DirectionalLight(0x4a6a8e, 1.8);
  fill.position.set(60, 20, 60);
  scene.add(fill);

  return {
    uniforms,
    update: (t) => { uniforms.uTime.value = t; },
    dispose: () => trash.forEach((o) => o.dispose()),
  };
}

const rand = (seed) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const L = 64;
const HB = 5.4;
export const halfBeamAt = (x) => (x <= 14 ? HB : HB * Math.max(0, 1 - ((x - 14) / 18) ** 2));

const hullShape = () => {
  const s = new THREE.Shape();
  s.moveTo(-L / 2, -HB * 0.95);
  s.lineTo(L * 0.22, -HB);
  s.bezierCurveTo(L * 0.4, -HB, L * 0.49, -HB * 0.45, L / 2, 0);
  s.bezierCurveTo(L * 0.49, HB * 0.45, L * 0.4, HB, L * 0.22, HB);
  s.lineTo(-L / 2, HB * 0.95);
  s.bezierCurveTo(-L / 2 - 1.6, HB * 0.6, -L / 2 - 1.6, -HB * 0.6, -L / 2, -HB * 0.95);
  return s;
};

const PALETTE = [0x1c2531, 0x232d3a, 0x2c3746, 0x37404c, 0x4a2c20, 0x5b3a24, 0x283a3a, 0x6f6c66, 0x1f2a36];

function stackSlots(fromX, toX, r) {
  const slots = [];
  for (let x = fromX; x <= toX; x += 2.7) {
    const hb = Math.min(halfBeamAt(x - 1.25), halfBeamAt(x + 1.25)) - 0.9;
    const rows = Math.max(0, Math.floor((hb * 2) / 1.06));
    const tiers = 2 + Math.floor(r() * 4);
    for (let row = 0; row < rows; row++) {
      const z = (row - (rows - 1) / 2) * 1.06;
      const h = Math.max(1, tiers - (r() < 0.3 ? 1 : 0) - (Math.abs(z) > hb - 1.3 ? 1 : 0));
      for (let tier = 0; tier < h; tier++) slots.push([x, 0.5 + tier * 0.97, z]);
    }
  }
  return slots;
}

function instancedStacks(slots, geo, mat, r) {
  const inst = new THREE.InstancedMesh(geo, mat, Math.max(slots.length, 1));
  const m = new THREE.Matrix4();
  const c = new THREE.Color();
  slots.forEach((p, i) => {
    m.makeTranslation(p[0], p[1], p[2]);
    inst.setMatrixAt(i, m);
    c.setHex(PALETTE[Math.floor(r() * PALETTE.length)]);
    inst.setColorAt(i, c);
  });
  inst.count = slots.length;
  return inst;
}

function buildAccommodation(keep) {
  const acc = new THREE.Group();
  const white = keep(new THREE.MeshStandardMaterial({ color: 0xaab2ba, roughness: 0.65, metalness: 0.15, transparent: true }));
  const dark = keep(new THREE.MeshStandardMaterial({ color: 0x1b232d, roughness: 0.6, metalness: 0.3, transparent: true }));
  const warm = keep(new THREE.MeshBasicMaterial({ color: 0xffb35c, transparent: true, opacity: 0.85 }));
  const boat = keep(new THREE.MeshStandardMaterial({ color: 0xff6a1a, roughness: 0.5, emissive: 0x401400, transparent: true }));
  const box = (w, h, d, mat, x, y, z = 0) => {
    const mesh = new THREE.Mesh(keep(new THREE.BoxGeometry(w, h, d)), mat);
    mesh.position.set(x, y, z);
    acc.add(mesh);
    return mesh;
  };
  box(6.5, 9, 9.6, white, -27, 4.5);
  box(5, 0.45, 12.8, white, -25.8, 9.2);
  box(4.2, 1.7, 9.6, white, -25.6, 10.3);
  box(2.4, 3.6, 2.6, dark, -29.8, 11, 0);
  [1.6, 3.4, 5.2, 7.0].forEach((y) => box(0.06, 0.42, 8.2, warm, -23.72, y));
  box(0.06, 0.7, 9.2, warm, -23.48, 10.45);
  const lb = new THREE.Mesh(keep(new THREE.CapsuleGeometry(0.55, 2.2, 4, 10)), boat);
  lb.rotation.z = Math.PI / 2 + 0.38;
  lb.position.set(-31.4, 3.6, 0);
  acc.add(lb);
  return { acc, mats: [white, dark, warm, boat], warm };
}

export function buildShip({ accommodation = false, seed = 7, extraCargo = false } = {}) {
  const group = new THREE.Group();
  const trash = [];
  const keep = (o) => { trash.push(o); return o; };
  const r = rand(seed);
  const shape = hullShape();
  const upperGeo = keep(new THREE.ExtrudeGeometry(shape, { depth: 2.6, bevelEnabled: false, curveSegments: 18 }));
  upperGeo.rotateX(Math.PI / 2);
  const deckMat = keep(new THREE.MeshStandardMaterial({ color: 0x1a222c, roughness: 0.85, metalness: 0.2 }));
  const hullMat = keep(new THREE.MeshStandardMaterial({ color: 0x24303e, roughness: 0.5, metalness: 0.45 }));
  const redMat = keep(new THREE.MeshStandardMaterial({ color: 0x431b14, roughness: 0.7, metalness: 0.2 }));
  const upper = new THREE.Mesh(upperGeo, [deckMat, hullMat]);
  const lower = new THREE.Mesh(upperGeo, [redMat, redMat]);
  lower.position.y = -2.6;
  lower.scale.set(0.99, 1, 0.97);
  group.add(upper, lower);
  group.add(new THREE.LineSegments(keep(new THREE.EdgesGeometry(upperGeo, 30)), keep(new THREE.LineBasicMaterial({ color: 0x7890a8, transparent: true, opacity: 0.7 }))));

  const boxGeo = keep(new THREE.BoxGeometry(2.5, 0.95, 1.0));
  const boxMat = keep(new THREE.MeshStandardMaterial({ roughness: 0.75, metalness: 0.25 }));
  group.add(instancedStacks(stackSlots(accommodation ? -20.5 : -27, 25.5, r), boxGeo, boxMat, r));
  let extra = null;
  if (extraCargo) {
    extra = new THREE.Group();
    extra.add(instancedStacks(stackSlots(-28.4, -22, r), boxGeo, boxMat, r));
    extra.scale.y = 0.001;
    group.add(extra);
  }

  const mastMat = keep(new THREE.MeshStandardMaterial({ color: 0x2c3743, roughness: 0.6, metalness: 0.5 }));
  const mastGeo = keep(new THREE.CylinderGeometry(0.12, 0.16, 1, 8));
  const addMast = (x, h) => {
    const m = new THREE.Mesh(mastGeo, mastMat);
    m.scale.y = h;
    m.position.set(x, h / 2, 0);
    group.add(m);
    return new THREE.Vector3(x, h, 0);
  };
  const bowTop = addMast(29.2, 5.4);
  const aftTop = accommodation ? addMast(-26.6, 14) : addMast(-29.4, 8.2);
  const dome = new THREE.Mesh(keep(new THREE.SphereGeometry(0.8, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2)), keep(new THREE.MeshStandardMaterial({ color: 0xc9d1d9, roughness: 0.4 })));
  dome.position.copy(aftTop);
  group.add(dome);
  const bar = new THREE.Mesh(keep(new THREE.BoxGeometry(0.14, 0.14, 4.2)), mastMat);
  bar.position.set(aftTop.x, aftTop.y - 1.4, 0);
  group.add(bar);

  const haloTex = keep(makeHalo());
  const nodeGeo = keep(new THREE.SphereGeometry(0.2, 12, 8));
  const nodeMat = keep(new THREE.MeshBasicMaterial({ color: 0x00f2fe }));
  const haloMat = keep(new THREE.SpriteMaterial({ map: haloTex, color: 0x00f2fe, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
  const pts = [
    [bowTop.x, bowTop.y + 0.25, 0], [aftTop.x, aftTop.y + 0.9, 0], [31.6, 1.1, 0], [-31.2, 1.3, 4.7], [-31.2, 1.3, -4.7],
    [0, 0.9, 5.45], [0, 0.9, -5.45], [16, 0.9, 5.42], [16, 0.9, -5.42], [-16, 0.9, 5.3], [-16, 0.9, -5.3], [aftTop.x, aftTop.y - 1.4, 2.1], [aftTop.x, aftTop.y - 1.4, -2.1],
  ];
  const sensors = pts.map(([x, y, z], i) => {
    const node = new THREE.Mesh(nodeGeo, nodeMat);
    const halo = new THREE.Sprite(haloMat);
    node.position.set(x, y, z);
    halo.position.set(x, y, z);
    group.add(node, halo);
    return { node, halo, phase: i * 0.7 };
  });

  const navLight = (hex, x, y, z, s) => {
    const sp = new THREE.Sprite(keep(new THREE.SpriteMaterial({ map: haloTex, color: hex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false })));
    sp.position.set(x, y, z);
    sp.scale.setScalar(s);
    group.add(sp);
  };
  navLight(0xff3b3b, 14, 1.5, -5.6, 1.4);
  navLight(0x3bff8a, 14, 1.5, 5.6, 1.4);
  navLight(0xffffff, bowTop.x, bowTop.y + 0.9, 0, 1.6);

  let accParts = null;
  if (accommodation) {
    accParts = buildAccommodation(keep);
    group.add(accParts.acc);
  }

  const baseY = 1.9;
  return {
    group,
    sensors,
    bowTop,
    aftTop,
    extra,
    accommodation: accParts,
    setSensorLevel: (k, t) => {
      sensors.forEach((s) => {
        const pulse = 0.8 + 0.35 * Math.sin(t * 2.4 + s.phase);
        s.node.scale.setScalar(Math.max(k, 0.001));
        s.halo.scale.setScalar(Math.max(k * pulse * 2.4, 0.001));
      });
    },
    update: (t) => {
      const hb = waveHeight(28, 0, t);
      const hs = waveHeight(-28, 0, t);
      const hp = waveHeight(0, 5, t);
      const hsb = waveHeight(0, -5, t);
      group.position.y = baseY + (hb + hs) * 0.25;
      group.rotation.z = Math.atan2(hb - hs, 56) * 0.6;
      group.rotation.x = -Math.atan2(hp - hsb, 10) * 0.35;
    },
    dispose: () => trash.forEach((o) => o.dispose()),
  };
}

export const createRenderer = (el, mobile) => {
  const renderer = new THREE.WebGLRenderer({ antialias: !mobile, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 1.75));
  renderer.setClearColor(FOG_HEX, 1);
  renderer.domElement.style.cssText = "display:block;width:100%;height:100%";
  el.appendChild(renderer.domElement);
  return renderer;
};

export const projectTo = (vec, object, camera, w, h, out) => {
  out.copy(vec).applyMatrix4(object.matrixWorld).project(camera);
  return { x: (out.x * 0.5 + 0.5) * w, y: (-out.y * 0.5 + 0.5) * h, behind: out.z > 1 };
};
